import React, { HTMLAttributes, useContext } from 'react'
import { Conversation, Chat, ChatContainerProps } from '@deal/chat-firebase'
import { IdentityContext } from '#src/containers/Identity'
import {
  useChatExpertQuery,
  useStartExpertConsumerConversationMutation
} from '#src/generated/graphql'
import { Link } from 'react-router-dom'
import { Avatar } from '@deal/components'
import EmailIcon from './email.svg'
import PhoneIcon from './phone.svg'
import StarIcon from './star.svg'
import styles from './styles.css'

interface ChatSidebarProps extends ChatContainerProps {
  expertId?: string
  style?: HTMLAttributes<HTMLDivElement>['style']
}

// TODO: Consolidate business logic between ChatSidebar & ChatTab
const ChatSidebar: React.SFC<ChatSidebarProps> = ({
  expertId,
  conversationId,
  style,
  ...props
}) => {
  const { myself } = useContext(IdentityContext)
  const { data } = useChatExpertQuery({
    variables: {
      businessUserId: expertId
    },
    skip: expertId === undefined
  })
  const [
    startExpertConsumerConversation,
    { data: startConversationData }
  ] = useStartExpertConsumerConversationMutation()

  const expert = data && data.businessUser
  const expertStats = expert && expert.expertStats
  const expertProfile = expert && expert.expertProfile
  const expertPhoto = expertProfile && expertProfile.profilePhoto && expertProfile.profilePhoto.url
  const expertPhone = expert && expert.assignedSmsAddress
  const expertEmail = expert && expert.assignedEmailAddress
  const expertName = expert && expert.displayName

  const handleConversationStarted = () => {
    if (myself && expert) {
      startExpertConsumerConversation({
        variables: {
          input: {
            consumerId: myself.id,
            expertId: expert.id
          }
        }
      })
    }
  }

  // If we are starting a conversation via the mutation, we will fall back to the newly created ID.
  // These should be the same, assuming whatever is passing in `props.conversationId` updates correctly.
  const newConversationId =
    startConversationData &&
    startConversationData.startExpertConsumerConversation.conversation &&
    startConversationData.startExpertConsumerConversation.conversation.id
  const derivedConversationdId = conversationId || newConversationId

  return (
    <div className={styles.sidebar} style={style}>
      {expert && expertProfile && (
        <div className={styles.sidebarHeader}>
          <Link
            to={`/experts/${expert.vanityId || expert.id}`}
            className={styles.expertAvatarAndRating}
          >
            <Avatar
              name={expertName!}
              src={expertPhoto || undefined}
              className={styles.expertAvatar}
              size={80}
            />
            {expertStats && expertStats.averageRating ? (
              <div className={styles.expertRating}>
                {expertStats.averageRating.toFixed(1)}{' '}
                <StarIcon className={styles.expertRatingStarIcon} />
              </div>
            ) : null}
          </Link>
          <div className={styles.expertBio}>
            <div className={styles.expertName}>{expertName!}</div>
            <div className={styles.expertHeadline}>{expertProfile.headline}</div>
            {expertEmail && (
              <div className={styles.expertEmail}>
                <EmailIcon className={styles.expertContactIcon} />
                {expertEmail}
              </div>
            )}
            {expertPhone && (
              <div className={styles.expertPhone}>
                <PhoneIcon className={styles.expertContactIcon} />
                {expertPhone}
              </div>
            )}
            <Link
              to={`/experts/${expert.vanityId || expert.id}`}
              className={styles.expertProfileLink}
            >
              View profile
            </Link>
          </div>
        </div>
      )}

      {myself && expert && (
        <Chat
          conversationId={derivedConversationdId}
          guestChatEnabled={false}
          onConversationStarted={handleConversationStarted}
          {...props}
        />
      )}
      {(!myself || !expert) && (
        <Conversation conversationId={derivedConversationdId} guestChatEnabled={true} {...props} />
      )}
    </div>
  )
}

export default ChatSidebar
